import { t as tr } from './i18n.js'
import { useEffect, useRef, type ReactNode } from 'react'
import { ArrowUp, ChevronDown, ChevronRight } from 'lucide-react'
import { usePref } from './prefs.js'

/**
 * A visually bounded, collapsible page section. State is per user (server
 * prefs) under `id`; `defaultOpen` decides the first visit. Each section has an
 * anchor (`#id`) so links can jump straight to it, and a "Top" button.
 * Long pages use closed-by-default sections; short pages pass defaultOpen.
 */
export function Section({ id, title, subtitle, defaultOpen = false, actions, children, className = '' }: {
  id: string; title: ReactNode; subtitle?: ReactNode; defaultOpen?: boolean; actions?: ReactNode; children: ReactNode; className?: string
}) {
  const [openPrefs, setOpen, loaded] = usePref<Record<string, boolean>>('sections', {})
  const open = openPrefs[id] ?? defaultOpen
  const ref = useRef<HTMLElement>(null)
  // Deep link: #id opens and scrolls to this section.
  useEffect(() => {
    if (location.hash === `#${id}`) {
      if (!open) setOpen({ ...openPrefs, [id]: true })
      setTimeout(() => ref.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 50)
    }
  }, [id, loaded, location.hash]) // open/openPrefs intentionally not deps: only react to hash + load
  return (
    <section id={id} ref={ref} className={`card section ${open ? 'open' : 'closed'} ${className}`}>
      <div className="sechead">
        <button className="link plain sectoggle" aria-expanded={open} aria-controls={`${id}-body`} data-tip={open ? tr("Collapse this section (remembered for you)") : tr("Expand this section (remembered for you)")} onClick={() => setOpen({ ...openPrefs, [id]: !open })}>
          {open ? <ChevronDown size={16} aria-hidden /> : <ChevronRight size={16} aria-hidden />}
        </button>
        <h2 className="sectitle" onClick={() => setOpen({ ...openPrefs, [id]: !open })}>{title}</h2>
        {subtitle && <span className="small muted secsub">{subtitle}</span>}
        <span className="spacer" />
        {actions}
        <button className="link plain small sectop" data-tip={tr("Jump to the top of the page")} aria-label={tr("Jump to top")} onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}><ArrowUp size={14} aria-hidden /> {tr("Top")}</button>
      </div>
      {open && <div id={`${id}-body`} className="secbody stack">{children}</div>}
    </section>
  )
}

/** Programmatic jump: opens (if closed) and scrolls to a section by id. */
export function jumpToSection(id: string) {
  history.replaceState(null, '', `#${id}`)
  window.dispatchEvent(new HashChangeEvent('hashchange'))
  setTimeout(() => document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 60)
}
